const DEFAULT_POST_AUTO_LOGIN_PATH = '/portal/document/userupload';

/** Allowed path prefixes after auto-login (matches app routes under Routes/Index.js). */
const ALLOWED_REDIRECT_PREFIXES = ['/portal/', '/user/', '/administrator/'];

/**
 * Returns a safe in-app path for post-login navigation, or the default path.
 * Mitigates open redirect (CWE-601) from user-controlled `page` query values.
 *
 * @param {string | null | undefined} rawPage
 * @returns {string}
 */
export function getSafePostAutoLoginPath(rawPage) {
  if (rawPage == null) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  let decoded = String(rawPage).trim();
  if (!decoded) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(decoded.replace(/\+/g, ' '));
      if (next === decoded) {
        break;
      }
      decoded = next;
    } catch {
      return DEFAULT_POST_AUTO_LOGIN_PATH;
    }
  }

  const lower = decoded.toLowerCase();
  if (
    lower.includes('http://') ||
    lower.includes('https://') ||
    lower.includes('//') ||
    lower.includes('\\') ||
    lower.includes('\0') ||
    lower.includes('\r') ||
    lower.includes('\n') ||
    lower.includes('\t')
  ) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(decoded)) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  if (decoded.includes('?') || decoded.includes('#')) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  const path = decoded.startsWith('/') ? decoded : `/${decoded}`;

  if (path.startsWith('//') || path.includes('@') || path.includes('..')) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  if (!/^\/[a-zA-Z0-9/_.-]+$/.test(path)) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  const isAllowed = ALLOWED_REDIRECT_PREFIXES.some(
    (prefix) => path.startsWith(prefix) || path === prefix.slice(0, -1),
  );
  if (!isAllowed) {
    return DEFAULT_POST_AUTO_LOGIN_PATH;
  }

  return path;
}

/**
 * Redirect after auto-login: validates `page`, resolves against the current origin, then assigns.
 * Keeps sanitization and navigation together for static analysis (CWE-601).
 *
 * @param {string | null | undefined} rawPage
 * @param {string | undefined} publicUrlBase - e.g. `process.env.PUBLIC_URL` without trailing slash
 */
export function assignPostAutoLoginLocation(rawPage, publicUrlBase) {
  const path = getSafePostAutoLoginPath(rawPage);
  const base = String(publicUrlBase || '').replace(/\/$/, '');
  const relativeTarget = `${base}${path}`;

  let resolved;
  try {
    resolved = new URL(relativeTarget, window.location.origin);
  } catch {
    resolved = new URL(`${base}${DEFAULT_POST_AUTO_LOGIN_PATH}`, window.location.origin);
    window.location.assign(resolved.href);
    return;
  }

  if (resolved.origin !== window.location.origin) {
    resolved = new URL(`${base}${DEFAULT_POST_AUTO_LOGIN_PATH}`, window.location.origin);
  }

  window.location.assign(resolved.href);
}
